// Call plugin.
import { trackActions } from '../interface/actions'
import * as actionTypes from '../interface/actionTypes'
import watchTrackEvents from './track'

// Libraries.
import { put, fork, takeEvery } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

/**
 * Sets up event listeners for Track Manager Events.
 * The events are turned into actions and dispatched
 *    to be handled by redux elsewhere.
 * @method watchTrackManagerEvents
 * @param  {Object} manager The webRTC Track Manager.
 */
export default function * watchTrackManagerEvents (manager) {
  /**
   * Create an event channel between the Track Manager and redux-saga.
   * This pushes its events into the redux-saga context.
   * */
  const managerChannel = eventChannel(emit => {
    return setListeners(manager, emit)
  })

  // https://github.com/redux-saga/redux-saga/issues/684
  // See user "kuy"'s second comment.
  // Using takeEvery to handle actions.
  // That way they are handled in a forked process and other actions don't get dropped.
  yield takeEvery(managerChannel, trackManagerEventsHandler)
}

function * trackManagerEventsHandler ({ track, action }) {
  // If it is a new Track, watch for its events.
  if (action.type === actionTypes.TRACK_ADDED) {
    yield fork(watchTrackEvents, track)
  }

  // Dispatch the action
  yield put(action)
}

/**
 * Sets up listeners for the Track Manager events.
 * Contains logic for how specific webRTC events should be
 *    parsed before being sent elsewhere. Split into a
 *    stand-alone function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   manager The Track Manager.
 * @param  {Function} emit    Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (manager, emit, END = 'END') {
  // Handle new Tracks.
  const trackAdded = id => {
    const track = manager.get(id)
    const state = track.getState()

    // Translate the track state from native WebRTC to SDK style.
    // This is to change the "muted" property to be what people generally know
    //    "muted" to be.
    state.sourceMuted = state.muted
    state.muted = !state.enabled

    delete state.enabled
    delete state.id

    /**
     * When a new track is added,
     * update redux state's webrtc.tracks array by adding track's state into it.
     */
    emit({
      track,
      action: trackActions.trackAdded(id, state)
    })
  }

  // Handle Tracks ending.
  const trackRemoved = id => {
    /**
     * When a track has ended,
     * update redux state's webrtc.tracks array
     * by specifying which track to remove by providing a track id.
     */
    emit({
      action: trackActions.trackRemoved(id)
    })
  }

  manager.on('add', trackAdded)
  manager.on('remove', trackRemoved)
  const unsubscribe = () => {
    manager.off('add', trackAdded)
    manager.off('remove', trackRemoved)
  }
  return unsubscribe
}
